'use client';

import { useEffect, useState, useRef, useCallback } from 'react';
import {
  ArrowUpRight,
  Download,
  MoreHorizontal,
  Trash2,
  Loader2,
  LoaderCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { usePathname, useRouter } from 'next/navigation';
import Highlighter from 'react-highlight-words';
import MigrateIcon from '/public/migrate.svg';
import HourglassIcon from '/public/hourglass.svg';
import MicroscopeIcon from '/public/microscope.svg';
import DataServiceIcon from '/public/data-service.svg';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import Link from 'next/link';
import { DeleteConfirmationDialog } from '@/components/thread/delete-confirmation-dialog';
import { useDeleteOperation } from '@/contexts/DeleteOperationContext';
import { Button } from '../ui/button';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import {
  PostMigrateThreadResponse,
  getThreadList,
  threadsStateUpdate,
} from '@/services/thread';
import { getMemoryUnread } from '@/services/memory';
import dayjs from 'dayjs';
import { deleteProject } from '@/services/thread';
import MemoryIcon from '/public/light1.svg';
import SearchIcon from '/public/search.svg';
import TempIcon from '/public/template.svg';
import AddThreadIcon from '/public/add.svg';
import LeftAgentIcon from '/public/left-agent-icon.svg';
import { debounce } from 'lodash-es';
import { triggerMemoryLibrary } from '@/components/memory-library/memory-library';
import useUserConfig from '@/hooks/deep-mining/use-user-config';
import { getDmclawCreditSpend } from '@/services/dmclaw-service';
import { EVT_UPDATE_PROJECT } from '@/constants/event';
import useThreadStatus from '@/hooks/use-thread-status';
import ConvMigrateModal from '../conv-migrate-modal';
import { useStores } from '@/store/hooks/useStore';
import { IconWrapper } from '../ui/icon-wrapper';
import { PurpleLoadingSvg } from '../ui/loading';
import CircleRightIcon from '/public/circle-right.svg';
import ErrorIcon from '/public/error.svg';
import CloseIcon from '/public/close-2.svg';
import { eventBus, EventTypes } from '@/lib/event-bus';
import { useI18n } from '@/hooks/use-i18n';

type IThreadListItem = THREAD.ThreadListItem;

const getTimeStr = (time: string) => {
  const messageDate = dayjs(time);
  const today = dayjs().startOf('day');

  // 如果是当天，显示时间 HH:mm
  if (messageDate.isSame(today, 'day')) {
    return messageDate.format('HH:mm');
  }
  // 非当天，显示日期 MM/DD
  return messageDate.format('MM/DD');
};

export function NavAgents() {
  const { t } = useI18n(['components', 'common']);
  const stores = useStores();
  const permissions = stores.userStore.permissions;
  const { state } = useSidebar();
  const [threads, setThreads] = useState<IThreadListItem[]>([]);
  const [filteredThreads, setFilteredThreads] = useState<IThreadListItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchInput, setShowSearchInput] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingThreadId, setLoadingThreadId] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<{
    threadId: string;
    projectId: string;
  } | null>(null);
  const pathname = usePathname();
  const router = useRouter();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<{
    id: string;
    name: string;
    threadId: string;
  } | null>(null);
  // const [isDeleting, setIsDeleting] = useState(false);
  // 通用操作状态
  const [isOperating, setIsOperating] = useState(false);
  const isNavigatingRef = useRef(false);
  const { performDelete, cancelDelete } = useDeleteOperation();
  const isPerformingActionRef = useRef(false);
  const [templateHasNewFlag, setTemplateHasNewFlag] = useState(false);
  const [memoryHasNewFlag, setMemoryHasNewFlag] = useState(false);
  const [openConvMigrateModal, setOpenConvMigrateModal] = useState(false);
  const onCloseConvMigrateModal = useCallback(() => {
    setOpenConvMigrateModal(false);
  }, []);
  const [threadId, setThreadId] = useState<string | null>(null);
  const pathnameRef = useRef(pathname);
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);
  const [dmclawPath, setDmclawPath] = useState('/dmclaw/download');

  // 实时搜索处理函数
  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);
      if (!query.trim()) {
        setFilteredThreads(threads);
        return;
      }

      const filtered = threads.filter((thread) =>
        thread.name.toLowerCase().includes(query.toLowerCase()),
      );
      setFilteredThreads(filtered);
    },
    [threads],
  );

  // 使用debounce防抖处理搜索
  const debouncedSearch = useCallback(
    debounce((query: string) => handleSearch(query), 300),
    [handleSearch],
  );

  // 处理搜索输入变化
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  // 清空搜索
  const clearSearch = () => {
    setSearchQuery('');
    setFilteredThreads(threads);
    setShowSearchInput(false);
  };

  // 切换搜索框显示状态
  const toggleSearchInput = () => {
    setShowSearchInput((prev) => {
      const newState = !prev;
      if (newState) {
        // 当显示搜索框时，重置搜索状态
        setSearchQuery('');
        setFilteredThreads(threads);
        // 使用setTimeout确保DOM已更新后再聚焦
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 0);
      }
      return newState;
    });
  };

  // 当threads更新时，更新filteredThreads
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredThreads(threads);
    } else {
      handleSearch(searchQuery);
    }
  }, [threads, searchQuery, handleSearch]);

  // Function to load threads data with associated projects
  const loadThreads = async (showLoading = true) => {
    try {
      if (showLoading) {
        setIsLoading(true);
      }

      const threadData = (await getThreadList()) || [];
      setThreads(threadData);
      // 更新过滤后的线程列表
      if (!searchQuery.trim()) {
        setFilteredThreads(threadData);
      } else {
        const filtered = threadData.filter((thread) =>
          thread.name.toLowerCase().includes(searchQuery.toLowerCase()),
        );
        setFilteredThreads(filtered);
      }
    } catch (err) {
      console.error('Error loading threads with projects:', err);
      // Set empty threads array on error
      setThreads([]);
      setFilteredThreads([]);
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  };
  const { updateUserConfig, getUserConfig } = useUserConfig();

  const handleTemplate = async (flag?: string) => {
    const _flag = flag || templateHasNewFlag;
    if (!_flag) return;
    try {
      await updateUserConfig({
        key: 'thread_template',
        value: 'false',
      });
      setTemplateHasNewFlag(false);
    } catch (err) {
      console.error('Error updating user config:', err);
    }
  };

  const handleResetThreadStore = () => {
    stores.threadStore.reset();
    stores.threadToolStore.reset();
    stores.threadWorkspaceStore.reset();
    stores.threadProjectStore.reset();
  };

  useEffect(() => {
    const init = async () => {
      const config = await getUserConfig(['thread_template']);
      setTemplateHasNewFlag(config['thread_template'] === 'true');

      if (pathname === '/template' && config['thread_template'] === 'true') {
        // 通过链接直接访问模版页面需要消除小红点
        handleTemplate(config?.['thread_template']);
      }
    };
    init();
  }, [pathname]);

  useEffect(() => {
    const handleApiNotify = (event: Event) => {
      const customEvent = event as CustomEvent;
      const eventData = customEvent?.detail;
      // 处理模板小红点
      if (
        eventData?.event === 'user_config_thread_template' &&
        pathnameRef.current !== '/template'
      ) {
        const { value } = eventData;
        setTemplateHasNewFlag(value === 'true');
      }
      // 处理记忆空间小红点：按 eventData.unread 决定显隐
      if (
        eventData?.event === 'memory_unread' &&
        pathnameRef.current !== '/memory-space'
      ) {
        setMemoryHasNewFlag(Boolean(eventData.unread));
      }
    };

    window.addEventListener('api-notify', handleApiNotify as EventListener);

    return () => {
      window.removeEventListener(
        'api-notify',
        handleApiNotify as EventListener,
      );
    };
  }, []);

  // 初次挂载从服务端拉一次红点状态，后续增量由 api-notify 推送
  useEffect(() => {
    if (pathnameRef.current === '/memory-space') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getMemoryUnread();
        if (cancelled) return;
        setMemoryHasNewFlag(Boolean(res?.unread));
      } catch (err) {
        console.error('getMemoryUnread failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 进入记忆空间后清除红点（服务端状态由页面内 init 接口消费）
  useEffect(() => {
    if (pathname === '/memory-space') {
      setMemoryHasNewFlag(false);
    }
  }, [pathname]);

  useEffect(() => {
    getDmclawCreditSpend()
      .then((data) => {
        if (Number(data?.total_spend) > 0) {
          setDmclawPath('/dmclaw');
        }
      })
      .catch(() => {});
  }, []);

  // Load threads dynamically from the API on initial load

  useEffect(() => {
    loadThreads(true);
  }, []);

  // 每次展开都获取最新数据
  useEffect(() => {
    (async () => {
      if (state !== 'collapsed') {
        await loadThreads(false);
      }
      clearSearch();
    })();
  }, [state]);

  // Listen for project-updated events to update the sidebar without full reload
  useEffect(() => {
    const handleProjectUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail) {
        const { projectId, updatedData } = customEvent.detail;

        if (projectId) {
          // Update just the name for the threads with the matching project ID
          setThreads((prevThreads) => {
            const updatedThreads = prevThreads.map((thread) =>
              thread.project_id === projectId
                ? {
                    ...thread,
                    ...updatedData,
                  }
                : thread,
            );

            // Return the threads without re-sorting immediately
            return updatedThreads;
          });
        }

        // Silently refresh in background to fetch updated timestamp and re-sort
        setTimeout(() => loadThreads(false), 1000);
      }
    };

    // Add event listener
    window.addEventListener(
      EVT_UPDATE_PROJECT,
      handleProjectUpdate as EventListener,
    );

    // Cleanup
    return () => {
      window.removeEventListener(
        EVT_UPDATE_PROJECT,
        handleProjectUpdate as EventListener,
      );
    };
  }, []);

  // 监听用户发送消息事件，立即隐藏对应会话的外部 Agent icon
  useEffect(() => {
    const unsubscribe = eventBus.on(
      EventTypes.USER_SENT_MESSAGE,
      ({ threadId: sentThreadId }: { threadId: string }) => {
        setThreads((prev) =>
          prev.map((t) =>
            t.thread_id === sentThreadId &&
            t.metadata?.last_source_msg_type === 'api-key'
              ? {
                  ...t,
                  metadata: { ...t.metadata, last_source_msg_type: 'system' },
                }
              : t,
          ),
        );
        setFilteredThreads((prev) =>
          prev.map((t) =>
            t.thread_id === sentThreadId &&
            t.metadata?.last_source_msg_type === 'api-key'
              ? {
                  ...t,
                  metadata: { ...t.metadata, last_source_msg_type: 'system' },
                }
              : t,
          ),
        );
      },
    );
    return unsubscribe;
  }, []);

  // Reset loading state when navigation completes (pathname changes)
  useEffect(() => {
    setLoadingThreadId(null);
  }, [pathname]);

  // Add event handler for completed navigation
  useEffect(() => {
    const handleNavigationComplete = () => {
      console.log('NAVIGATION - Navigation event completed');
      document.body.style.pointerEvents = 'auto';
      isNavigatingRef.current = false;
    };

    window.addEventListener('popstate', handleNavigationComplete);

    return () => {
      window.removeEventListener('popstate', handleNavigationComplete);
      // Ensure we clean up any leftover styles
      document.body.style.pointerEvents = 'auto';
    };
  }, []);

  // Reset isNavigatingRef when pathname changes
  useEffect(() => {
    isNavigatingRef.current = false;
    document.body.style.pointerEvents = 'auto';
  }, [pathname]);

  const handleUpdateThreadState = (threadId: string) => {
    threadsStateUpdate('completed', threadId);
  };

  // 批量任务状态判断辅助函数
  const isBatchTaskFinished = (state: string) => {
    return (
      state === 'async_tag_task_completed' ||
      state === 'async_tag_task_failed' ||
      state === 'async_task_completed' ||
      state === 'async_task_failed' ||
      state === 'async_task_stop'
    );
  };

  //检查当前会话是否需要取消标记
  useEffect(() => {
    // 如果有会话列表
    if (threads.length > 0) {
      // 遍历所有会话，检查是否有需要标记的
      threads.forEach((thread) => {
        // 使用统一的批量任务状态判断函数
        if (isBatchTaskFinished(thread.state)) {
          // 判断当前是否在该会话页面
          const isActive = pathname?.includes(thread.thread_id);

          // 如果是当前页面，则标记为已查看
          if (isActive) {
            handleUpdateThreadState(thread.thread_id);
          }
        }
      });
    }
  }, [threads, pathname, isBatchTaskFinished]);

  // Function to handle thread click with loading state
  const handleThreadClick = (
    e: React.MouseEvent<HTMLDivElement>,
    threadId: string,
    url: string,
  ) => {
    e.preventDefault();
    // 获取当前点击的会话
    const thread = threads.find((t) => t.thread_id === threadId);

    // 如果是已完成或失败的打标任务，调用接口更新状态
    if (thread && isBatchTaskFinished(thread.state)) {
      handleUpdateThreadState(threadId);
    }
    // 重置store
    handleResetThreadStore();

    setLoadingThreadId(threadId);
    router.push(url);
  };

  // Function to handle thread deletion
  const handleDeleteProject = async (
    projectId: string,
    projectName: string,
    threadId: string,
  ) => {
    setProjectToDelete({ id: projectId, name: projectName, threadId });
    setIsDeleteDialogOpen(true);
  };
  const handleOpenConvMigrateModal = (threadId: string) => {
    setThreadId(threadId);
    setOpenConvMigrateModal(true);
  };
  const confirmDelete = async () => {
    if (!projectToDelete || isPerformingActionRef.current) return;

    // Mark action in progress
    isPerformingActionRef.current = true;
    setIsOperating(true);

    // Close dialog first for immediate feedback
    setIsDeleteDialogOpen(false);

    const projectId = projectToDelete.id;
    const isActive = pathname?.includes(projectToDelete.threadId);

    // Use the centralized deletion system with completion callback
    await performDelete(
      projectId,
      isActive,
      async () => {
        // Delete the thread
        await deleteProject(projectId);

        // Update the thread list
        setThreads((prev) => prev.filter((t) => t.project_id !== projectId));

        // Show success message
        toast.success(t('components:sidebar.conversationDeleteSuccess'));
      },
      // Completion callback to reset local state
      () => {
        setProjectToDelete(null);
        setIsOperating(false);
        isPerformingActionRef.current = false;
        if (isActive) {
          router.push('/dashboard');
        }
      },
    );
  };

  const onConfirmConvMigrate = async (data: PostMigrateThreadResponse) => {
    // 防止重复操作
    if (isPerformingActionRef.current) return;

    // 标记迁移操作进行中
    isPerformingActionRef.current = true;
    setIsOperating(true);

    try {
      // 判断当前是否在被迁移的会话页面
      const isActive = pathname?.includes(threadId || '');

      // 获取成功迁移的会话ID列表
      const successThreadIds = data.success;

      // 更新本地线程列表，移除成功迁移的会话
      if (successThreadIds.length > 0) {
        setThreads((prev) =>
          prev.filter((t) => !successThreadIds.includes(t.thread_id)),
        );
      }

      // 根据页面状态决定是否跳转
      if (isActive && successThreadIds.includes(threadId || '')) {
        // 如果当前会话被成功迁移，跳转到dashboard
        router.push('/dashboard');
      }
    } finally {
      // 清空所有相关状态
      setThreadId(null);
      setIsOperating(false);
      isPerformingActionRef.current = false;
    }
  };
  const isCollapsed = state === 'collapsed';
  useThreadStatus({
    threads,
    setThreads,
    collapsed: isCollapsed,
    refreshThreads: () => loadThreads(false),
  });

  // 线程状态配置映射表
  const THREAD_STATUS_CONFIG = {
    running: {
      color: '#7171ee',
      text: t('components:sidebar.executing'),
      icon: LoaderCircle,
      spin: true,
    },
    ask_human: {
      color: '#7171ee',
      text: t('components:sidebar.waitingReply'),
      icon: HourglassIcon,
      spin: false,
    },
    batch_task_running: {
      color: '#7171ee',
      text: t('components:sidebar.batchTaskExecuting'),
      icon: PurpleLoadingSvg,
      spin: true,
    },
    batch_task_completed: {
      color: '#15C65C',
      text: t('components:sidebar.batchTaskCompleted'),
      icon: CircleRightIcon,
      spin: false,
    },
    batch_task_interrupted: {
      color: '#FF0000',
      text: t('components:sidebar.taskStopped'),
      icon: ErrorIcon,
      spin: false,
    },
  } as const;

  // 获取线程状态类型 TODO 之后修改
  const getThreadStatusType = (state: string) => {
    if (state === 'running') return 'running';
    if (state === 'ask_human' || state === 'wait_editing') {
      return 'ask_human';
    }
    if (state === 'async_tag_task_running' || state === 'async_task_running') {
      return 'batch_task_running';
    }
    if (
      state === 'async_tag_task_completed' ||
      state === 'async_task_completed'
    ) {
      return 'batch_task_completed';
    }
    if (
      state === 'async_tag_task_failed' ||
      state === 'async_task_failed' ||
      state === 'async_task_stop'
    ) {
      return 'batch_task_interrupted';
    }
    return null;
  };

  const renderThreadStatus = (thread: IThreadListItem) => {
    const statusType = getThreadStatusType(thread.state);

    // 如果有匹配的状态配置，使用配置渲染
    if (statusType && THREAD_STATUS_CONFIG[statusType]) {
      const config = THREAD_STATUS_CONFIG[statusType];
      const Icon = config.icon;

      return (
        <span
          className="flex items-center gap-1 text-[10px]"
          style={{ color: config.color }}
        >
          <Icon className={`h-3 w-3 ${config.spin ? 'animate-spin' : ''}`} />
          {config.text}
        </span>
      );
    }

    // 默认显示描述信息
    return (
      <span className="text-[12px] text-muted-foreground truncate pr-2">
        {thread.description || ' '}
      </span>
    );
  };
  const renderThreads = (list: IThreadListItem[]) => {
    return (
      <>
        {list.map((thread) => {
          // Check if this thread is currently active
          const isActive = pathname?.includes(thread.thread_id) || false;
          const isThreadLoading = loadingThreadId === thread.thread_id;
          const url = `/agents/${thread.thread_id}`;
          const isRunning = thread.state === 'running';

          return (
            <SidebarMenuItem key={`thread-${thread.thread_id}`}>
              <SidebarMenuButton
                asChild
                className={cn(
                  'py-2 pl-2 h-auto',
                  isActive
                    ? 'bg-sidebar-accent text-accent-foreground font-medium'
                    : '',
                )}
              >
                <div
                  onClick={(e) => {
                    // 如果是当前 thread 不执行任何操作
                    if (isActive) return;
                    handleThreadClick(e, thread.thread_id, url);
                  }}
                  className="w-full group/item"
                >
                  <div className="flex flex-col w-full gap-1">
                    {/* 第一行：loading+name 和时间 */}
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-1 overflow-hidden">
                        {isThreadLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
                        ) : null}
                        {thread.metadata?.last_source_msg_type ===
                          'api-key' && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="flex-shrink-0 flex items-center justify-center w-4 h-4 rounded-full bg-[#7171EE] cursor-default">
                                  <LeftAgentIcon className="w-2.5 h-2.5 text-white" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>
                                  {t('components:sidebar.externalAgentSession')}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        <span className="text-[12px] font-medium truncate">
                          {searchQuery ? (
                            <Highlighter
                              highlightClassName="bg-[#7171EE33] text-[#7171ee]"
                              textToHighlight={thread.name}
                              searchWords={[searchQuery]}
                            />
                          ) : (
                            thread.name
                          )}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                        {getTimeStr(thread.last_message_create_at)}
                      </span>
                    </div>

                    {/* 第二行：描述信息和操作按钮 */}
                    <div className="flex items-center justify-between w-full">
                      {renderThreadStatus(thread)}
                      <div className="flex-shrink-0 opacity-0 group-hover/item:opacity-100 transition-opacity">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className="flex items-center justify-center h-5 w-5 rounded-full hover:bg-muted cursor-pointer"
                              onClick={(e) => e.stopPropagation()}
                              data-state={
                                showShareModal &&
                                selectedItem?.threadId === thread.thread_id
                                  ? 'open'
                                  : undefined
                              }
                            >
                              <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                              <span className="sr-only">
                                {t('components:more')}
                              </span>
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            className="w-56 rounded-lg"
                            side="right"
                            align="start"
                            onCloseAutoFocus={(e) => e.preventDefault()}
                          >
                            {permissions.includes('thread:new_tab') && (
                              <DropdownMenuItem asChild>
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <ArrowUpRight className="text-muted-foreground" />
                                  <span>
                                    {t('components:sidebar.openInNewTab')}
                                  </span>
                                </a>
                              </DropdownMenuItem>
                            )}
                            {permissions.includes('thread:migrate') &&
                              !isRunning && (
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenConvMigrateModal(
                                      thread.thread_id,
                                    );
                                  }}
                                >
                                  <MigrateIcon className="text-muted-foreground" />
                                  <span>
                                    {t(
                                      'components:sidebar.migrateConversation',
                                    )}
                                  </span>
                                </DropdownMenuItem>
                              )}
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteProject(
                                  thread.project_id,
                                  thread.name,
                                  thread.thread_id,
                                );
                              }}
                            >
                              <Trash2 className="text-muted-foreground" />
                              <span>{t('common:delete')}</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                </div>
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </>
    );
  };
  const showNewThreadMenu = permissions.includes('thread:create');
  const showTemplateMenu = permissions.includes('template');
  const showMemoryMenu = permissions.includes('memory');
  const showSkillMenu = permissions.includes('skills');
  const showDmclawMenu = permissions.includes('dm_claw');
  const showDataServiceMenu = permissions.includes('data_service');
  const showMenu =
    showNewThreadMenu ||
    showTemplateMenu ||
    showMemoryMenu ||
    showSkillMenu ||
    showDmclawMenu ||
    showDataServiceMenu;
  return (
    <SidebarGroup
      className={cn(
        isCollapsed ? '' : 'p-0',
        'flex flex-col flex-1 overflow-hidden',
      )}
    >
      <div className="flex-shrink-0 flex justify-between items-center">
        {!isCollapsed && showMenu && (
          <div className="w-full border-b-[1px] border-[#E5E5E5] pb-1 px-4">
            {showNewThreadMenu && (
              <SidebarMenuButton asChild>
                <Button
                  className="w-full text-sm bg-background hover:bg-background text-foreground mb-3 justify-start h-10 cursor-pointer"
                  onClick={() => router.push('/dashboard')}
                >
                  <AddThreadIcon className="w-4 h-4" />
                  <span>{t('components:sidebar.newConversation')}</span>
                </Button>
              </SidebarMenuButton>
            )}
            {showTemplateMenu && (
              <SidebarMenuButton asChild>
                <div
                  className={cn(
                    'w-full text-sm bg-[rgb(244, 244, 245)] hover:!bg-[#E3E3E3] text-foreground mb-3 justify-between cursor-pointer h-10 pl-3 pr-[9px]',
                    pathname === '/template' ? 'bg-[#E3E3E3]' : '',
                  )}
                  onClick={() => {
                    handleTemplate();
                    router.push('/template');
                  }}
                >
                  <div className="flex items-center gap-2">
                    <TempIcon className="w-4 h-4" />
                    <span>{t('components:sidebar.taskTemplate')}</span>
                  </div>
                  {templateHasNewFlag && (
                    <div className="ml-2 w-2 h-2 rounded-full bg-[#FFB73D]" />
                  )}
                </div>
              </SidebarMenuButton>
            )}
            {showMemoryMenu && (
              <SidebarMenuButton asChild>
                <div
                  className={cn(
                    'w-full text-sm bg-[rgb(244, 244, 245)] hover:!bg-[#E3E3E3] text-foreground mb-3 justify-between cursor-pointer h-10 pl-3 pr-[9px]',
                    pathname === '/memory-space' ? 'bg-[#E3E3E3]' : '',
                  )}
                  onClick={() => router.push('/memory-space')}
                >
                  <div className="flex items-center gap-2">
                    <MemoryIcon className="w-4 h-4" />
                    <span>{t('components:sidebar.memorySpace')}</span>
                  </div>
                  {memoryHasNewFlag && (
                    <div className="ml-2 w-2 h-2 rounded-full bg-[#FFB73D]" />
                  )}
                </div>
              </SidebarMenuButton>
            )}
            {showSkillMenu && (
              <SidebarMenuButton asChild>
                <div
                  className={cn(
                    'w-full text-sm bg-[rgb(244, 244, 245)] hover:!bg-[#E3E3E3] text-foreground mb-3 justify-start cursor-pointer h-10 px-3',
                    pathname === '/skill' ? 'bg-[#E3E3E3]' : '',
                  )}
                  onClick={() => router.push('/skill')}
                >
                  <MicroscopeIcon className="w-4 h-4" />
                  <span>{t('components:sidebar.skillSpace')}</span>
                </div>
              </SidebarMenuButton>
            )}
            {showDmclawMenu && (
              <SidebarMenuButton asChild>
                <div
                  className={cn(
                    'w-full text-sm bg-[rgb(244, 244, 245)] hover:!bg-[#E3E3E3] text-foreground mb-3 justify-start cursor-pointer h-10 px-3',
                    pathname?.startsWith('/dmclaw') ? 'bg-[#E3E3E3]' : '',
                  )}
                  onClick={() => router.push(dmclawPath)}
                >
                  <Download className="w-4 h-4" />
                  <span>{t('components:sidebar.dmclaw')}</span>
                </div>
              </SidebarMenuButton>
            )}
            {showDataServiceMenu && (
              <SidebarMenuButton asChild>
                <div
                  className={cn(
                    'w-full text-sm bg-[rgb(244, 244, 245)] hover:!bg-[#E3E3E3] text-foreground mb-3 justify-start cursor-pointer h-10 px-3',
                    pathname?.startsWith('/data-service') ? 'bg-[#E3E3E3]' : '',
                  )}
                  onClick={() => router.push('/data-service')}
                >
                  <DataServiceIcon className="w-4 h-4" />
                  <span>{t('components:sidebar.dataService')}</span>
                </div>
              </SidebarMenuButton>
            )}
          </div>
        )}
      </div>

      {!isCollapsed && !isLoading && !!threads?.length && (
        <div className="flex-shrink-0 flex items-center justify-between px-4">
          <div className="text-[#00000040] text-[12px] h-10 leading-10">
            {t('components:sidebar.chat')}
          </div>
          {showSearchInput ? (
            <div className="flex items-center rounded-md px-2 py-1 w-[70%] border-[1px] border-[#E6E7EB] bg-white">
              <SearchIcon className="w-4 h-4 text-gray-400 mr-0.5" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchInputChange}
                placeholder={t('components:sidebar.searchTask')}
                className="bg-transparent border-none outline-none text-[12px] w-full mr-2"
              />
              <CloseIcon
                className="w-4 h-4 text-gray-400 cursor-pointer hover:text-gray-600"
                onClick={clearSearch}
              />
            </div>
          ) : (
            <div className="cursor-pointer" onClick={toggleSearchInput}>
              <SearchIcon className="w-4 h-4" />
            </div>
          )}
        </div>
      )}
      <SidebarMenu className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        {isCollapsed && showNewThreadMenu && (
          <SidebarMenuItem>
            <Tooltip>
              <TooltipTrigger asChild>
                <SidebarMenuButton asChild>
                  <div
                    onClick={() => router.push('/dashboard')}
                    className="flex items-center justify-center bg-background"
                  >
                    <AddThreadIcon className="w-4 h-4" />
                  </div>
                </SidebarMenuButton>
              </TooltipTrigger>
              <TooltipContent>
                {t('components:sidebar.newConversation')}
              </TooltipContent>
            </Tooltip>
          </SidebarMenuItem>
        )}

        <div className="px-4">
          {isCollapsed ? null : isLoading ? (
            // Show skeleton loaders while loading
            Array.from({ length: 3 }).map((_, index) => (
              <SidebarMenuItem key={`skeleton-${index}`}>
                <SidebarMenuButton>
                  <div className="h-4 w-4 bg-sidebar-foreground/10 rounded-md animate-pulse"></div>
                  <div className="h-3 bg-sidebar-foreground/10 rounded w-3/4 animate-pulse"></div>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))
          ) : threads.length > 0 ? (
            // Show all threads with project info
            <>
              {searchQuery && filteredThreads.length === 0 ? (
                <SidebarMenuButton
                  disabled
                  asChild
                  className="bg-[#F4F4F5] hover:bg-[#F4F4F5] hover:text-muted-foreground active:bg-[#F4F4F5] active:text-muted-foreground data-[state=open]:hover:bg-[#F4F4F5] data-[state=open]:hover:text-muted-foreground"
                >
                  <div className="text-muted-foreground flex items-center justify-center text-xs">
                    <span>{t('common:noData')}</span>
                  </div>
                </SidebarMenuButton>
              ) : (
                renderThreads(searchQuery ? filteredThreads : threads)
              )}
            </>
          ) : (
            <SidebarMenuButton
              disabled
              asChild
              className="bg-[#F4F4F5] hover:bg-[#F4F4F5] hover:text-muted-foreground active:bg-[#F4F4F5] active:text-muted-foreground data-[state=open]:hover:bg-[#F4F4F5] data-[state=open]:hover:text-muted-foreground"
            >
              <div className="text-muted-foreground flex items-center justify-center text-xs">
                <span>{t('components:sidebar.noHistory')}</span>
              </div>
            </SidebarMenuButton>
          )}
        </div>
      </SidebarMenu>

      {projectToDelete && (
        <DeleteConfirmationDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => {
            setIsDeleteDialogOpen(false);
            cancelDelete();
          }}
          onConfirm={confirmDelete}
          threadName={projectToDelete.name}
          isDeleting={isOperating}
        />
      )}
      {openConvMigrateModal && (
        <ConvMigrateModal
          open={openConvMigrateModal}
          onClose={onCloseConvMigrateModal}
          threadId={threadId}
          onConfirm={onConfirmConvMigrate}
        />
      )}
    </SidebarGroup>
  );
}
